import React, { useState, useEffect } from 'react';
import { Card, ListGroup, Alert, Badge, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { getDateFnsLocale, safeFormatDate, formatDateWithLocale } from '../shared/utils';
import { fetchWeatherData } from '../shared/weatherApi';

export default function MainLocationsTab({ parsedData, isTemplate = false }) {
  const { t, i18n } = useTranslation();
  const [mainLocations, setMainLocations] = useState([]);
  const [weatherData, setWeatherData] = useState({});
  const [loadingWeather, setLoadingWeather] = useState(false);

  useEffect(() => {
    if (parsedData && parsedData.days) {
      const locations = extractMainLocations(parsedData);
      setMainLocations(locations);
      
      // Fetch weather data for each location
      if (locations.length > 0) {
        fetchWeatherForLocations(locations);
      }
    }
  }, [parsedData]);

  // Extract accommodation locations from plan data
  const extractMainLocations = (data) => {
    if (!data.days || !Array.isArray(data.days)) return [];

    const accommodationPeriods = [];
    
    // Process each day to find accommodations
    data.days.forEach((day, dayIndex) => {
      if (day.activities && Array.isArray(day.activities)) {
        day.activities.forEach(activity => {
          if (activity.type === 'accommodation' && activity.location) {
            // Extract city and country from location
            const location = activity.location;
            let city = '';
            let country = '';
            
            if (typeof location === 'object') {
              // Try to extract city and country from address or specific fields
              city = location.city || '';
              country = location.country || '';
              
              // If city/country not directly available, try to parse from address
              if (!city || !country) {
                const address = location.address || '';
                const addressParts = address.split(',').map(part => part.trim());
                
                if (addressParts.length >= 2) {
                  // Assume last part is country, second to last is city
                  country = country || addressParts[addressParts.length - 1];
                  city = city || addressParts[addressParts.length - 2];
                }
              }
            } else if (typeof location === 'string') {
              // Parse string location
              const addressParts = location.split(',').map(part => part.trim());
              if (addressParts.length >= 2) {
                country = addressParts[addressParts.length - 1];
                city = addressParts[addressParts.length - 2];
              }
            }
            
            // Only add if we have at least city or country
            if (city || country) {
              const locationString = [city, country].filter(Boolean).join(', ');
              
              // Use proper date fallbacks
              const startDate = activity.startDateTime || activity.checkInTime || day.date;
              const endDate = activity.endDateTime || activity.checkOutTime || day.date;
              
              accommodationPeriods.push({
                id: activity.id || `${dayIndex}_${activity.name}`,
                name: activity.name,
                location: locationString,
                city: city,
                country: country,
                startDate: startDate,
                endDate: endDate,
                dayIndex: dayIndex
              });
            }
          }
        });
      }
    });

    // Sort by start date and consolidate consecutive periods in same location
    const sortedAccommodations = accommodationPeriods.sort((a, b) => {
      const dateA = new Date(a.startDate);
      const dateB = new Date(b.startDate);
      return dateA - dateB;
    });

    // Group consecutive accommodations in the same location
    const consolidatedLocations = [];
    let currentGroup = null;

    sortedAccommodations.forEach(accommodation => {
      if (!currentGroup || currentGroup.location !== accommodation.location) {
        // Start new group
        if (currentGroup) {
          consolidatedLocations.push(currentGroup);
        }
        currentGroup = {
          location: accommodation.location,
          city: accommodation.city,
          country: accommodation.country,
          startDate: accommodation.startDate,
          endDate: accommodation.endDate,
          startDayIndex: accommodation.dayIndex,
          endDayIndex: accommodation.dayIndex,
          accommodations: [accommodation]
        };
      } else {
        // Extend current group
        currentGroup.endDate = accommodation.endDate;
        currentGroup.endDayIndex = accommodation.dayIndex;
        currentGroup.accommodations.push(accommodation);
      }
    });

    // Add the last group
    if (currentGroup) {
      consolidatedLocations.push(currentGroup);
    }

    return consolidatedLocations;
  };

  // Local weather cache with 8-hour expiration
  const getLocalWeatherCache = () => {
    try {
      const cached = localStorage.getItem('mainLocationsWeatherCache');
      if (!cached) return {};
      
      const parsedCache = JSON.parse(cached);
      const now = Date.now();
      const eightHours = 8 * 60 * 60 * 1000; // 8 hours in milliseconds
      
      // Remove expired entries
      const validCache = {};
      Object.keys(parsedCache).forEach(key => {
        if (parsedCache[key].timestamp && (now - parsedCache[key].timestamp < eightHours)) {
          validCache[key] = parsedCache[key];
        }
      });
      
      return validCache;
    } catch (error) {
      console.error('Error reading weather cache:', error);
      return {};
    }
  };

  const setLocalWeatherCache = (key, data) => {
    try {
      const cache = getLocalWeatherCache();
      cache[key] = {
        data,
        timestamp: Date.now()
      };
      localStorage.setItem('mainLocationsWeatherCache', JSON.stringify(cache));
    } catch (error) {
      console.error('Error saving weather cache:', error);
    }
  };

  // Fetch weather data for all locations
  const fetchWeatherForLocations = async (locations) => {
    setLoadingWeather(true);
    const weatherResults = {};
    const localCache = getLocalWeatherCache();

    try {
      for (const location of locations) {
        if (location.city && location.country) {
          try {
            const startDateStr = safeFormatDate(location.startDate, 'date');
            const endDateStr = safeFormatDate(location.endDate, 'date');
            const cacheKey = `${location.city}_${location.country}_${startDateStr}_${endDateStr}`;
            const groupKey = `${location.location}_${startDateStr}_${endDateStr}`;
            
            // Check local cache first
            if (localCache[cacheKey]) {
              console.log(`Using 8-hour cached weather for ${location.location}`);
              weatherResults[groupKey] = localCache[cacheKey].data;
              continue;
            }

            // Prepare date range for the location
            const startDate = new Date(startDateStr);
            const endDate = new Date(endDateStr);
            const today = new Date();
            const daysDifference = Math.ceil((startDate - today) / (1000 * 60 * 60 * 24));
            
            // Helper: generate full list of dates within the location range (inclusive)
            const generateDateRange = (start, end) => {
              const dates = [];
              const cur = new Date(start);
              while (cur <= end) {
                const y = cur.getFullYear();
                const m = String(cur.getMonth() + 1).padStart(2, '0');
                const d = String(cur.getDate()).padStart(2, '0');
                dates.push(`${y}-${m}-${d}`);
                cur.setDate(cur.getDate() + 1);
              }
              return dates;
            };
            const dateRange = generateDateRange(startDate, endDate);
            
            // If outside API window, build placeholder days so UI still shows full range
            if (daysDifference > 16 || daysDifference < 0) {
              const placeholderDays = dateRange.map(date => ({
                date,
                condition: t('planDetail.mainLocations.unableToFetch'),
                tempMin: null,
                tempMax: null,
                precipitation: 0
              }));
              const placeholderData = { days: placeholderDays };
              weatherResults[groupKey] = placeholderData;
              setLocalWeatherCache(cacheKey, placeholderData);
              continue;
            }

            const cityCountry = `${location.city}, ${location.country}`;
            const weather = await fetchWeatherData({
              city: cityCountry,
              days: 16, // Get maximum forecast range
              forceRefresh: false
            });

            // Map forecast by date for quick lookup
            const forecastByDate = new Map(
              weather.forecast.map(day => [day.date, day])
            );

            // Build full list for the location's date range, filling missing days with placeholders
            const fullDays = dateRange.map(date =>
              forecastByDate.get(date) || {
                date,
                condition: t('planDetail.mainLocations.unableToFetch'),
                tempMin: null,
                tempMax: null,
                precipitation: 0
              }
            );

            const weatherData = { days: fullDays };
            weatherResults[groupKey] = weatherData;
            setLocalWeatherCache(cacheKey, weatherData);
          } catch (error) {
            console.error(`Weather fetch error for ${location.location}:`, error);
            const startDateStr = safeFormatDate(location.startDate, 'date');
            const endDateStr = safeFormatDate(location.endDate, 'date');
            const groupKey = `${location.location}_${startDateStr}_${endDateStr}`;
            weatherResults[groupKey] = { error: t('planDetail.mainLocations.unableToFetch') };
          }
        }
      }
    } catch (error) {
      console.error('General weather fetch error:', error);
    } finally {
      setLoadingWeather(false);
    }

    setWeatherData(weatherResults);
  };

  // Get weather icon based on condition
  const getWeatherIcon = (condition) => {
    if (!condition) return '🌤️';
    
    const conditionLower = condition.toLowerCase();
    if (conditionLower.includes('sunny') || conditionLower.includes('clear')) return '☀️';
    if (conditionLower.includes('cloudy') || conditionLower.includes('overcast')) return '☁️';
    if (conditionLower.includes('partly')) return '⛅';
    if (conditionLower.includes('rain') || conditionLower.includes('shower')) return '🌧️';
    if (conditionLower.includes('snow')) return '❄️';
    if (conditionLower.includes('storm') || conditionLower.includes('thunder')) return '⛈️';
    if (conditionLower.includes('fog') || conditionLower.includes('mist')) return '🌫️';
    
    return '🌤️'; // Default
  };



  // Format date for daily forecast display using date-fns with locale support
  const formatForecastDate = (dateStr) => {
    try {
      const date = new Date(dateStr);
      const locale = getDateFnsLocale(i18n.language);
      return format(date, 'EEE, MMM d', { locale });
    } catch (error) {
      return dateStr;
    }
  };

  const formatDateRange = (startDate, endDate) => {
    try {
      // Use locale-aware date formatting
      const start = formatDateWithLocale(startDate, 'PP');
      const end = formatDateWithLocale(endDate, 'PP');
      
      // Check if formatting failed
      if (!start || start === 'Invalid date' || !end || end === 'Invalid date') {
        return t('planDetail.mainLocations.invalidDate');
      }
      
      if (start === end) {
        return start;
      }
      return `${start} - ${end}`;
    } catch (error) {
      console.error('Date formatting error:', error, 'Start:', startDate, 'End:', endDate);
      return t('planDetail.mainLocations.invalidDate');
    }
  };

  if (!parsedData) {
    return (
      <Card>
        <Card.Body>
          <Alert variant="info">{t('planDetail.mainLocations.noPlanData')}</Alert>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card>
      <Card.Body>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h4 className="mb-0">{t('planDetail.mainLocations.mainLocations')}</h4>
          <Badge bg="secondary">
            {mainLocations.length} {mainLocations.length !== 1 ? t('planDetail.mainLocations.locations') : t('planDetail.mainLocations.location')}
          </Badge>
        </div>
        
        {mainLocations.length === 0 ? (
          <Alert variant="info">
            <i className="bi bi-info-circle me-2"></i>
            {t('planDetail.mainLocations.noAccommodationsFound')}
          </Alert>
        ) : (
          <ListGroup variant="flush">
            {mainLocations.map((location, index) => (
              <ListGroup.Item key={index} className="py-3">
                <div className="d-flex justify-content-between align-items-start flex-wrap">
                  <div className="flex-grow-1 me-3">
                    <div className="d-flex justify-content-between align-items-start flex-wrap">
                      <h6 className="mb-1 fw-bold">
                        <i className="bi bi-geo-alt-fill me-2 text-primary"></i>
                        {location.location}
                      </h6>
                      <Badge bg="success" className="ms-2 flex-shrink-0">
                        {(() => {
                          try {
                            const startDateStr = safeFormatDate(location.startDate, 'date');
                            const endDateStr = safeFormatDate(location.endDate, 'date');
                            if (!startDateStr || !endDateStr || startDateStr === 'Invalid date' || endDateStr === 'Invalid date') {
                              return t('planDetail.mainLocations.invalidDates');
                            }

                            // Calculate nights using calendar dates only (ignore time/timezone).
                            const [startY, startM, startD] = startDateStr.split('-').map(Number);
                            const [endY, endM, endD] = endDateStr.split('-').map(Number);
                            const startUtc = Date.UTC(startY, startM - 1, startD);
                            const endUtc = Date.UTC(endY, endM - 1, endD);
                            const dayDiff = Math.round((endUtc - startUtc) / (1000 * 60 * 60 * 24));

                            // Same-day stay counts as 1 night; otherwise use checkout-checkin day diff.
                            const totalNights = dayDiff <= 0 ? 1 : dayDiff;
                            return `${totalNights} ${totalNights !== 1 ? t('planDetail.mainLocations.nights') : t('planDetail.mainLocations.night')}`;
                          } catch (error) {
                            console.error('Nights calculation error:', error);
                            return t('planDetail.mainLocations.invalidDates');
                          }
                        })()}
                      </Badge>
                    </div>
                    {!isTemplate && (
                    <div className="text-muted small mb-2">
                      <i className="bi bi-calendar-range me-1"></i>
                      {formatDateRange(location.startDate, location.endDate)}
                    </div>
                    )}
                    
                    {/* Weather Information - hidden in template mode */}
                    {!isTemplate && (
                    <div className="mt-2">
                      {loadingWeather ? (
                        <div className="d-flex align-items-center">
                          <Spinner size="sm" className="me-2" />
                          <small className="text-muted">{t('planDetail.mainLocations.loadingWeather')}</small>
                        </div>
                      ) : weatherData[`${location.location}_${safeFormatDate(location.startDate,'date')}_${safeFormatDate(location.endDate,'date')}`] ? (
                        weatherData[`${location.location}_${safeFormatDate(location.startDate,'date')}_${safeFormatDate(location.endDate,'date')}`].error ? (
                          <small className="text-muted">
                            <i className="bi bi-cloud-slash me-1"></i>
                            {weatherData[`${location.location}_${safeFormatDate(location.startDate,'date')}_${safeFormatDate(location.endDate,'date')}`].error}
                          </small>
                        ) : (
                          /* Daily Forecast Only - No Summary */
                          weatherData[`${location.location}_${safeFormatDate(location.startDate,'date')}_${safeFormatDate(location.endDate,'date')}`].days && (
                            <div className="mt-2 p-2 bg-light rounded">
                              {weatherData[`${location.location}_${safeFormatDate(location.startDate,'date')}_${safeFormatDate(location.endDate,'date')}`].days.map((day, dayIndex) => (
                                <div key={dayIndex} className="d-flex justify-content-between align-items-center py-1 mb-1 bg-white rounded border px-2">
                                  <div className="d-flex align-items-center">
                                    <span className="me-2">{getWeatherIcon(day.condition)}</span>
                                    <div>
                                      <div className="fw-bold small">{formatForecastDate(day.date)}</div>
                                      <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                                        {day.condition}
                                      </div>
                                    </div>
                                  </div>
                                  <div className="text-end">
                                    <div className="fw-bold small">
                                      {day.tempMin}°C - {day.tempMax}°C
                                    </div>
                                    {day.precipitation > 0 && (
                                      <div className="text-info" style={{ fontSize: '0.75rem' }}>
                                        <i className="bi bi-droplet"></i> {day.precipitation}mm
                                      </div>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )
                        )
                      ) : null}
                    </div>
                    )}
                    {location.accommodations.length > 1 && (
                      <div className="mt-2">
                        <small className="text-muted">
                          <i className="bi bi-house-door me-1"></i>
                          {location.accommodations.length} {location.accommodations.length !== 1 ? t('planDetail.mainLocations.accommodations') : t('planDetail.mainLocations.accommodation')}:
                        </small>
                        <ul className="list-unstyled ms-3 mt-1">
                          {location.accommodations.map((acc, accIndex) => (
                            <li key={accIndex} className="small text-muted">
                              • {acc.name}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              </ListGroup.Item>
            ))}
          </ListGroup>
        )}
        
        <div className="mt-3">
          <small className="text-muted">
            <i className="bi bi-info-circle me-1"></i>
            {t('planDetail.mainLocations.infoText')}
          </small>
        </div>
      </Card.Body>
    </Card>
  );
}